import {FORMS, IPendingValidity, MxFormElement, OFormElementInit} from "/@lib@/commons/forms.js";
import {BaseElement, BaseElementAsync, OSkinableInit} from "/@back@/commons/basis.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {IDepotResUiEnv} from "/@lib@/store/depot.js";
import {IResFormIdentCtx, JCidMetas, resPath} from "/@lib@/store/res.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";
import {Button, OButtonInit} from "/@back@/commons/widgets/buttons.js";
import {UtSelector} from "/@back@/store/dialogs/utSelector.js";
import {UtBrowserFetcher} from "/@back@/store/views/utBrowser.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JNodeShortProps, URLTREE} from "/@lib@/store/urlTree.js";
import {ResIdentFieldSetArea} from "/@back@/store/areas/resViewAreas.js";
import {AREAS} from "/@lib@/commons/areas.js";


/**
 * Widget racine d'édition des champs identificatoires avec contruction de deux champs
 * standard.
 */
export interface ResIdentFieldSet {
	initialize(init: OResIdentFieldSetInit): this;
}

export interface OResIdentFieldSetInit extends IResFormIdentCtx, OSkinableInit {
	reg: IReg<IDepotResUiEnv>
	area: ResIdentFieldSetArea
}

export class ResIdentFieldSet extends MxFormElement(BaseElementAsync) implements IPendingValidity {

	config: OResIdentFieldSetInit;

	folderInput?: ResParentPathInput;
	leafNameInput?: HTMLInputElement;
	anchor?: HTMLElement;

	targetPath: resPath | undefined;
	targetState: Promise<void> | 'exist' | 'free' | 'inTrash' | undefined;

	protected async _initialize(init: OResIdentFieldSetInit) {
		this.config = init;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		init.reg.installSkin("form-control-areas", sr);
		this._initAndInstallSkin(this.localName, init);

		if (!init.area.noFolderSelector && init.folderUi !== "hidden") {
			this.folderInput = <ResParentPathInput id="folderInput" î={{
				reg: init.reg,
				folderPath: init.folderPath,
				disabled: init.folderUi === "readOnly"
			} as OResParentPathInputInit}/> as ResParentPathInput;
			this.folderInput.addEventListener("change", this.onInputChange);
			sr.appendChild(init.buildControlLabel ? AREAS.buildControlLabel(this.folderInput, "folder", "Dossier") : this.folderInput);
		}

		if (!init.area.noResNameField && init.leafUi !== "hidden") {
			const readOnly = init.leafUi === "readOnly";
			this.leafNameInput = <input id="nameInput" type="text"
																	value={init.leafName || ""}
																	disabled={readOnly ? true : undefined}
																	required/> as HTMLInputElement;
			this.leafNameInput.addEventListener("input", this.onInputChange);
			sr.appendChild(init.buildControlLabel ? AREAS.buildControlLabel(this.leafNameInput, "resName", "Nom") : this.leafNameInput);
			this.initDefaultCaretInputName();
		}
		this.anchor = this.leafNameInput || this.folderInput;
		if (!this.anchor) this.tabIndex = -1;

		sr.appendChild(<slot/>);

		this._initializeForm(init);

		if (init.area.customInputs) {
			this.addEventListener("change", this.refreshValidity);
			this.addEventListener("input", this.refreshValidity);
			const wait: Promise<any>[] = [];
			for (let area of init.area.customInputs) {
				wait.push(area.loadBody(init));
			}
			(await Promise.all(wait)).forEach((elt) => this.appendChild(elt));
		}
	}

	protected initDefaultCaretInputName() {
		if (!this.leafNameInput.disabled) {
			const pos = this.leafNameInput.value.indexOf('.'); //On essaye de se placer avant le 1er '.'
			this.leafNameInput.setSelectionRange(0, pos >= 0 ? pos : this.leafNameInput.value.length);
		}
	}

	/** Nom de la res issu du widget standard (si pas éliminé via init.area.noResNameField) */
	resName(): string | undefined {
		return this.leafNameInput?.value || this.config.leafName;
	}

	/** resPath du dossier parent issu du widget standard (si pas éliminé via init.area.noFolderSelector) */
	folderPath(): resPath | undefined {
		return this.folderInput?.getParentPath() || this.config.folderPath;
	}

	/** Appelable par les widgets fils pour reconstruire le path (lors de fillJson() notamment). */
	buildVersionedPath(version: string, resName?: string, folder?: resPath | null) {
		return URLTREE.appendToPath(folder !== undefined ? folder : this.folderPath(), resName || this.resName()) + '@' + version;
	}

	/** Appelable par les widgets fils pour déclencher une éventuelle reeval de la dispo de la cible par le widget parent. */
	planRevalid() {
		this.setValidity(null);
		this.addPendingValidity(this);
		this.config.callback?.identState("pending");
	}

	extractJson(parent: JCidMetas): boolean {
		if (parent.path) {
			if (this.folderInput) {
				const parentPath = URLTREE.extractParentPath(parent.path);
				if (parentPath != null) this.folderInput.setParentPath(parentPath);
			}
			if (this.leafNameInput) {
				const leafName = URLTREE.extractUnversionedLeafName(parent.path);
				if (leafName != null) {
					this.leafNameInput.value = leafName;
					this.initDefaultCaretInputName();
					this.refreshValidity();
				}
			}
		}
		FORMS.jsonToForm(parent, this);
		return false;
	}

	fillJson(parent: JCidMetas, root: Jsonisable) {
		//Champs customs
		FORMS.formToJson(this, parent, root);
		//Construction du path
		if (this.config.area.pathBuilder) {
			this.config.area.pathBuilder(parent, this);
		} else {
			//Note: si pas pathBuilder, les widgets standards sont obligatoires, pas de version.
			parent.path = URLTREE.appendToPath(this.folderPath(), this.resName());
		}
	}

	async computeValidity(): Promise<any> {
		if (this.validity.badInput) return; //erreur synchrone
		if (await FORMS.checkAsyncValidity(this, true)) {
			//Les chamsp custom sont OK.
			if (this.validity.badInput) return; //racecond erreur synchrone
			const meta: JCidMetas = {};
			this.fillJson(meta, meta);
			this.targetPath = meta.path;
			if (meta.path != null) {
				//console.log("computeValidity for:::",this.targetPath);
				this.targetState = this.config.reg.env.universe.adminUrlTree.nodeInfos(meta.path, "&excludeAll&props=n*trashed").then((ni) => {
					if (meta.path !== this.targetPath) return; //racecond
					if (this.validity.badInput) return; //racecond erreur synchrone
					this.targetState = ni ? ni.trashed ? 'inTrash' : 'exist' : 'free';
					//console.log("this.targetState:::", this.targetState, ni);
					switch (this.targetState) {
					case "exist":
						if (this.config.resUpdatable !== "always") {
							this.setMsg("Un contenu existe déjà à cet emplacement, changez de dossier ou de nom.", 'warning');
						} else {
							this.setMsg("Un contenu existe à cet emplacement, il va être remplacé.", 'info');
						}
						break;
					case "inTrash":
						if (this.config.resUpdatable === "never") {
							this.setMsg("Un contenu existe déjà à cet emplacement (en état supprimé), changez de dossier ou de nom.", 'warning');
						} else if (this.config.resUpdatable === "ifInTrash") {
							this.setMsg("Un contenu en état supprimé existe à cet emplacement, il va être remplacé.", 'info');
						}
						break;
					case "free":
						break;
					}
				});
				return this.targetState;
			} else {
				//Ne devrait jamais arriver...
				this.targetState = undefined;
				this.setMsg("Impossible de définir l'identité de la ressource", 'error');
			}
		}
	}

	protected setMsg(msg: string, level: 'info' | 'warning' | 'error') {
		//customError = erreur Asynchrone
		if (level !== 'info') this.setValidity({customError: true}, msg, this.anchor);
		if (this.config.callback) {
			if (this.targetState instanceof Promise) {
				this.config.callback.identState('pending');
			} else if (level === 'error' || this.targetState == null) {
				this.config.callback.identState("invalid", null, msg, level);
			} else {
				this.config.callback.identState(this.targetState, this.targetPath, msg, level);
			}
		}
	}

	protected _refresh() {
		super._refresh();
		this.refreshValidity();
	}

	onInputChange(this: HTMLElement) {
		DOMSH.findHost<ResIdentFieldSet>(this).refreshValidity();
	}

	refreshValidity() {
		if (this.leafNameInput) {
			const st = URLTREE.isValidResName(this.leafNameInput.value);
			if (st === true) {
				this.planRevalid();
			} else {
				//badInput = erreur synchrone
				this.setValidity({badInput: true}, st, this.leafNameInput);
				this.config.callback?.identState("invalid", null, st, 'warning');
			}
		} else {
			this.planRevalid();
		}
	}
}

REG.reg.registerSkin('store-res-ident-fieldset', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-width: 0;
		min-height: fit-content;
		flex-direction: column;
		padding: 0.5rem;
		width: 40em;
		max-width: calc(100% - 1rem);
	}

	:host(:focus) {
		outline: none !important;
	}

	:focus-visible {
		outline: var(--focus-outline);
	}

	#folderInput {
		margin-bottom: .5em;
	}

	#nameInput:disabled {
		background-color: transparent;
		border: none;
	}

`);

customElements.define('store-res-ident-fieldset', ResIdentFieldSet);


// /**
//  *
//  */
// export interface ResNameInput {
// 	initialize(init: OResNameInputInit): this;
// }
//
// export interface OResNameInputInit extends OFormElementInit {
//
// 	reg?: IReg<IDepotResUiEnv>
//
// 	/** Nom de la res à utiliser avec son extension. */
// 	leafName?: string
//
// 	/** Si une res avec le path cible existe déjà dans le dépot, autorise-t-on sa modif. Valeur par défaut : never */
// 	resUpdatable?: 'always' | 'ifInTrash' | 'never'
// }
//
// export class ResNameInput extends MxFormElement(BaseElement) {
//
// 	protected _initialize(init: OResNameInputInit) {
// 		this._attach(this.localName, init,
// 			<input type="text" value={init.leafName || ""}/>
// 		);
// 		this._initializeForm(init);
// 	}
// }
//
// // REG.reg.registerSkin('store-res-name-input', 1, /* language=CSS */ `
// // 	:host {
// // 		flex: 1;
// // 		display: flex;
// // 		min-width: 0;
// // 		min-height: 0;
// // 		flex-direction: column;
// // 	}
// // `);
//
// customElements.define('store-res-name-input', ResNameInput);


/**
 *
 */
export interface ResParentPathInput {
	initialize(init: OResParentPathInputInit): this;
}

export interface OResParentPathInputInit extends OFormElementInit {

	reg?: IReg<IDepotResUiEnv>

	/**
	 * Path du dossier parent de la res.
	 */
	folderPath?: resPath
}

export class ResParentPathInput extends MxFormElement(BaseElement) {

	reg: IReg<IDepotResUiEnv>;

	protected _parentPath: resPath;

	pathField: HTMLElement;
	pathBtn: Button;

	protected _initialize(init: OResParentPathInputInit) {
		this.reg = this.findReg(init);
		this._parentPath = init.folderPath || "";
		this.pathField = <span id="folder"/>;
		this.pathBtn = <Button id="folderBtn" î={{
			reg: this.reg,
			label: "...",
			title: "Sélectionner un dossier...",
			uiContext: "dialog",
		} as OButtonInit} onclick={this.onClickSel}/> as Button;
		this._attach(this.localName, init, this.pathField, this.pathBtn);
		this._initializeForm(init);
	}

	getParentPath(): resPath {
		return this._parentPath || URLTREE.DEFAULT_PATH_ROOT;
	}

	setParentPath(path: resPath) {
		if (this._parentPath !== path && this.dispatchEvent(new CustomEvent("change", {cancelable: true, bubbles: true}))) {
			this._parentPath = path;
			this.refresh();
		}
	}

	async onClickSel(this: Button, ev: Event) {
		if (this.disabled) return;
		const me = DOMSH.findHost<ResParentPathInput>(this);
		const ct = new UtSelector().initialize({
			reg: me.reg,
			initialPath: me._parentPath,
			utBrowser: {
				fetcher: new UtBrowserFetcher(me.reg.env.universe.adminUrlTree).setFilter("isFolder"),
			}
		});
		const parentNode = await POPUP.showMenuFromEvent<JNodeShortProps>(ct, ev, me, null, {initWidth: '25em'}).onNextClose();
		if (parentNode) me.setParentPath(parentNode.permaPath);
	}

	protected _refresh() {
		super._refresh();
		DOM.setTextContent(this.pathField, this.reg.env.resTypes.humanPath(this._parentPath));
		this.pathBtn.disabled = this.disabled;
	}
}

REG.reg.registerSkin('store-res-parentpath-input', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-width: 0;
		min-height: 0;
	}

	#folder {
		flex: 1;
	}

	#folderBtn {
		margin: 0;
	}
`);

customElements.define('store-res-parentpath-input', ResParentPathInput);